import Button from "@amzn/meridian/button";
import Card, { CardHeader } from "@amzn/meridian/card";
import Checkbox from "@amzn/meridian/checkbox";
import Column from "@amzn/meridian/column";
import Heading from "@amzn/meridian/heading";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import React, { useEffect, useState } from "react";
import Currency from "../common/currency";
import { addStationVarianceForRemittance, getRemittanceList } from "../remittance/network/RemittanceAPI";
import ExcessVarianceList from "../common/excessVarianceList";
import { getStationCode } from "../../network/NetworkUtil";
import AlertModal from "./alertModal";
import AppLoader from "../common/appLoader";
import Alert from "@amzn/meridian/alert";
import { RemittanceStatus, VarianceType } from "../common/remittanceV2Constants";
import { Metric, PROCESS_NAME, StringConstants } from "../common/constants";
import { getReadableLabel, getRemittanceRequestParams } from "../common/commonUtils";
import { publishCount } from "../../MetricUtility";

const CreateRemittanceV2Modal = (props) => {
    const {onClose} = props;
    const [hasExcessVariance, setHasExcessVariance] = useState();
    const [excessVarianceList, setExcessVarianceList] = useState([]);
    const [actualAmount, setActualAmount] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState();
    const [alertModal, setAlertModal] = useState({});

    useEffect(() => {
        getRemittanceList(getRemittanceRequestParams()).then(response => {
            if (response.status === 200) {
                const {data: {remittanceList}} = response;
                processRemittanceList(remittanceList);
            } else {
                setAlertModal({show: true, type: "error", title:"Error", message:"Failed to fetch bank deposits"});
            }
            setIsLoading(false);
        })
        .catch(() => {
            setAlertModal({show: true, type: "error", title:"Error", message:"Unexpected error occurred"});
            setIsLoading(false);
        });
    }, []);


    useEffect(() => {
        if (!hasExcessVariance) {
            setExcessVarianceList([]);
        }
    }, [hasExcessVariance]);

    /**
     * This method will call the addStationVarianceForRemittance API.
     */
    const onCreateRemittance = () => {
        setErrorMessage();
        setIsLoading(true);
        addStationVarianceForRemittance(createRequestData()).then(response => {
            if (response.status === 200) {
                const { data } = response;
                if (data.responseCode === StringConstants.SUCCESS) {
                    setAlertModal({
                        show: true, 
                        type: "success", 
                        title: "Success", 
                        message: "Successfully created the remittance"
                    })
                } else {
                    setErrorMessage("Error: " + getReadableLabel(data.errorCode));
                }
            } else {
                setErrorMessage("Failed to create the remittance");
            }
            setIsLoading(false);
        }).catch(() => {
            setErrorMessage("Unexpected Error Occurred");
            setIsLoading(false);
        }).finally(() => {
            publishCount(PROCESS_NAME, Metric.CreateRemittance);
        });
    }

    /**
     * Method to create request param for Create Remittance request
     * @returns Request object
     */
    const createRequestData = () => {
        const stationVariance = excessVarianceList.map(variance => {
            let reason = variance.varianceReason.reasonCode;
            if (variance.ttLink) {
                reason = reason + StringConstants.SEPARATOR + variance.ttLink;
            }
            return {
                type: VarianceType.EXCESS,
                amount: variance.amount,
                reason: reason
            }
        });
        return {
            stationCode: getStationCode(),
            stationVarianceList: stationVariance
        }
    }

    /**
     * Method to process the remittance list from the response to set the state of modal
     * @param {List} remittances 
     */
    function processRemittanceList(remittances) {
        const filteredRemittance = remittances.filter(remittance => remittance.status === RemittanceStatus.CREATED);
        
        if (filteredRemittance.length) {
            const totalActualAmount = {value: 0, unit: props.currencyUnit};
            for (let i = 0; i < filteredRemittance.length; i++) {
                let remittance = filteredRemittance[i];
                totalActualAmount.value += remittance.actualAmount.value;
            }
            setActualAmount(totalActualAmount);
        } else {
            setAlertModal({show: true, title:"Info", message: "Currently there is no deposit to create remittance"});
        }
    }

    return (
        <React.Fragment>
            {isLoading && <AppLoader />}
            {actualAmount && 
                <Modal
                    title="Create Remittance"
                    open={true}
                    scrollContainer="viewport"
                    describedById="modal-description"
                >
                    <Column spacing="medium">
                        {errorMessage && <Alert type="error">{errorMessage}</Alert>}
                        <Row widths={270} alignmentHorizontal="center">
                            <Card>
                                <CardHeader>
                                    <Heading level={4} type="b100">
                                        <b>Actual Amount</b>
                                    </Heading>
                                </CardHeader>
                                <Currency amount={actualAmount} />
                            </Card>
                        </Row>
                        <Column spacing="small">
                            <Row spacingInset="medium none small">
                                <Checkbox
                                    checked={hasExcessVariance}
                                    onChange={setHasExcessVariance}
                                >
                                    <Text>Has extra cash to submit</Text>
                                </Checkbox>
                            </Row>

                            {hasExcessVariance && (
                                <ExcessVarianceList
                                    currency={props.currencyUnit}
                                    excessVarianceList={excessVarianceList}
                                    setExcessVarianceList={setExcessVarianceList}
                                />
                            )}
                        </Column>
                    </Column>

                    <ModalFooter>
                        <Row alignmentHorizontal="right" widths="fit">
                            <Button type="secondary" size="large" disabled={isLoading} onClick={onClose}>
                                Cancel
                            </Button>
                            <Button type="primary" size="large" disabled={isLoading} onClick={onCreateRemittance}>
                                Create
                            </Button>
                        </Row>
                    </ModalFooter>
                </Modal>
            }
            <AlertModal {...alertModal} onClose={() => {window.location.reload()}} />
        </React.Fragment>
    );
};

export default CreateRemittanceV2Modal;



// WEBPACK FOOTER //
// ./src/component/modal/createRemittanceV2Modal.js